import { CircularProgress, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import apiCall from "../../../../utils/apiCall";
import SettingsModal from '../Modal/settingsCustomModal';
import { ReactComponent as RoleIcon } from '../../../../svg/adminRoleIcon.svg';
import { ReactComponent as Arrow } from '../../../../svg/arrow.svg';
import ZoneDetails from "./ZoneDetails";
import ZoneOptions from "./ZoneOptions";
import UpdateZone from "./UpdateZone";
import DeleteZone from "./DeleteZone";

interface ZoneResponse {
    data: Zone[];
}

interface Zone {
  ZoneId: number;
  ZoneName: string;
  StateId: number;
  State: State;
}

interface State {
  StateId: number;
  StateName: string;
  CountryId: number;
  Country?: any;
  Id: number;
  CreatedBy?: any;
  ModifiedBy?: any;
  UserIpAddress?: any;
  CreateDate: string;
  LastModified: string;
  ActiveStatus: boolean;
  IsApprovalRequired?: any;
  RouteId?: any;
  ApprovalTypeId?: any;
  IsDeleted: boolean;
}

const ZoneContent = () => {
    const [openZoneDetails, setOpenZoneDetails] = useState(false);
    const [openUpdateZoneModal, setOpenUpdateZoneModal] = useState(false);
    const [openDeleteZoneModal, setOpenDeleteZoneModal] = useState(false);
    const [zoneName, setZoneName] = useState('');
    const [zoneId, setZoneId] = useState(0);
    const [stateId, setStateId] = useState(0);
    const [stateName, setStateName] = useState('');

    const handleOpenZoneDetails = () => setOpenZoneDetails(true);
    const handleCloseZoneDetails = () => setOpenZoneDetails(false);

    const handleOpenUpdateZoneModal = () => setOpenUpdateZoneModal(true);
    const handleCloseUpdateZoneModal = () => setOpenUpdateZoneModal(false);

    const handleOpenDeleteZoneModal = () => setOpenDeleteZoneModal(true);
    const handleCloseDeleteZoneModal = () => setOpenDeleteZoneModal(false);

    const handleDelete = () => {
        handleCloseZoneDetails();
        handleOpenDeleteZoneModal();
    };
    
    const handleUpdate = () => {
        handleCloseZoneDetails();
        handleOpenUpdateZoneModal();
    };

    const getZones = async (): Promise<ZoneResponse> => {
        return (await apiCall().get('/Zone/GetZones')); 
    };

    const { data, isLoading, isError, error } = useQuery(['get-zones'], getZones, {
        retry: 0,
        keepPreviousData: true,
        enabled: true,
    });

    const getZoneMessage = error as AxiosError;
    const getZoneErrorMessage = getZoneMessage?.message;

    const handleOpen = (zone: Zone) => {
        setZoneName(zone?.ZoneName);
        setZoneId(zone?.ZoneId);
        setStateId(zone?.StateId);
        setStateName(zone?.State?.StateName);
        handleOpenZoneDetails();
    };

    return (
        <>
            {isLoading ? (
                <CircularProgress 
                    sx={{
                        fontSize: '1rem',
                        marginInline: 'auto',
                        display: 'block',
                        marginBlock: '2rem',
                    }}
                />
            ) : isError ? (
                <StyledError>{getZoneErrorMessage}</StyledError>
            ) : (
            <StyledContainer>
                <TotalWrapper>
                    <span>Total</span>
                    <TotalNumberStyle>{data?.data?.length}</TotalNumberStyle>
                </TotalWrapper>
                {data?.data && data?.data?.length > 0 &&
                    data?.data.map((data, index) => (
                    <RoleWrapper onClick={() => handleOpen(data)} key={index}>
                        <div>
                            <RoleIconWrapper><RoleIcon /></RoleIconWrapper>
                            <RoleText>{data?.ZoneName}</RoleText>
                            <Active>Active</Active>
                            <DateWrapper></DateWrapper>
                        </div>
                        <ArrowIconWrapper>
                            <Arrow />
                        </ArrowIconWrapper>
                    </RoleWrapper>
                ))}

                <SettingsModal 
                    open={openZoneDetails}
                    cancel={handleCloseZoneDetails}
                    maxWidth='712px'
                    header={zoneName}
                    content={<ZoneDetails state={stateName} zoneName={zoneName}/>}
                    card={<ZoneOptions 
                        handleUpdate={handleUpdate} 
                        handleDelete={handleDelete}
                    />}
                />

                <SettingsModal
                    open={openUpdateZoneModal}
                    cancel={handleCloseUpdateZoneModal}
                    maxWidth='480px'
                    header='Update Zone'
                    showrightIcon={false}
                    content={<UpdateZone 
                        zoneId={zoneId} 
                        handleClose={handleCloseUpdateZoneModal} 
                        zoneName={zoneName}
                        state={stateId}
                    />}   
                />

                <SettingsModal
                    open={openDeleteZoneModal}
                    cancel={handleCloseDeleteZoneModal}
                    maxWidth='653px'
                    header='Delete Zone'
                    showrightIcon={false}
                    content={<DeleteZone 
                        zoneId={zoneId} 
                        zoneName={zoneName} 
                        handleClose={handleCloseDeleteZoneModal}
                    />}   
                />
            </StyledContainer>
            )}
        </> 
    )
}

export default ZoneContent;

const StyledContainer = styled('div')`
    margin-block: 3.8rem;
    margin-inline: 3.5rem;
`

const TotalNumberStyle = styled('span')`
    background-color: #F1F3F4;
    border-radius: 0.2rem;
    margin-inline-start: 0.4rem;
    padding-inline: 0.2rem;
`

const TotalWrapper = styled('div')`
    color: #4E5A62;
    font-weight: 500;
`

const RoleWrapper = styled('div')`
    display: flex;
    justify-content: space-between;
    border: 0.1rem solid #E1E1E1;
    margin-block: 0.6rem;
    padding-inline: 1.2rem;
    padding-block: 1.2rem;
    border-radius: 0.5rem;
    width: 100%;
    text-align: left;
    background-color: transparent;
`

const RoleIconWrapper = styled('span')`
    background-color: #FFF4DE;
    border-radius: 0.1rem;
    padding-inline: 0.4rem;
`

const RoleText = styled('span')`
    font-weight: 600;
    font-size: 16px;
    margin-inline: 0.8rem;
`

const Active = styled('span')`
    font-size: 14px;
    font-weight: 500;
    background-color: #E0FAEE;
    border-radius: 0.4rem;
    padding-inline: 0.5rem;
    color: #11643C;
`

const DateWrapper = styled('div')`
    margin-block-start: 0.8rem;
`

const ArrowIconWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: center;
`

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`